/**
 * Quotas API Service
 *
 * Typed client for quota endpoints:
 *  - GET  /api/v1/me/quotas         (member)
 *  - GET  /api/v1/quotas            (admin list)
 *  - POST /api/v1/quotas            (create)
 *  - PATCH /api/v1/quotas/:id       (update)
 *  - DELETE /api/v1/quotas/:id      (delete)
 *  - POST /api/v1/quotas/:id/override (grant override)
 *  - DELETE /api/v1/quotas/:id/overrides/:overrideId (revoke override)
 *  - POST /api/v1/quotas/:id/reset  (manual reset)
 *
 * Matches contracts/api-contracts.yaml.
 */

import type { QuotaEnvelope, QuotaEnvelopeBar, QuotaSignalState } from '@opus-ia/contracts';
import { apiClient } from '../api-client';

export type { QuotaEnvelope, QuotaEnvelopeBar };

// ---------------------------------------------------------------------------
// Types (matches shared_schemas.TokenUsage)
// ---------------------------------------------------------------------------

export interface QuotaOverride {
  id: string;
  quota_id: string;
  additional_input_tokens: number;
  additional_output_tokens: number;
  reason: string;
  granted_by: string;
  granted_by_name?: string;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

export interface TokenUsage {
  id?: string;
  scope: 'org' | 'team' | 'user';
  scope_id: string;
  scope_name?: string;
  model_id?: string | null;
  model_name?: string | null;
  period_type: string;
  period_start: string;
  period_end: string;
  limit_input_tokens: number;
  limit_output_tokens: number;
  used_input_tokens: number;
  used_output_tokens: number;
  remaining_input_tokens: number;
  remaining_output_tokens: number;
  /** 0.0 to 1.0 */
  usage_percentage: number;
  next_reset_at: string | null;
  reset_strategy?: ResetStrategy | null;
  active_overrides: QuotaOverride[];
  // --- FEAT-054 TASK-011 (barra) --- optional signals, absent from servers before FEAT-054
  block_at_input_tokens?: number;
  block_at_output_tokens?: number;
  warn_base_input_tokens?: number;
  warn_base_output_tokens?: number;
  /** 0.0 to 1.0+ over D (usage_percentage stays over L) */
  warn_percentage?: number;
  state?: QuotaSignalState;
  grace_mode?: 'after' | 'before';
  grace_pct?: number;
  // --- fim FEAT-054 TASK-011 (barra) ---
}

export interface ResetStrategy {
  carry_over: boolean;
  carry_over_percentage?: number;
}

/**
 * GET /api/v1/me/quotas wire (FEAT-036): `{ quotas, envelope }` -- no `data` key, so the apiClient
 * returns the whole body. envelope.most_restrictive.pct and envelope.bars[].pct are 0..100,
 * whereas TokenUsage.usage_percentage is 0..1.
 */
export interface GetMyQuotasResponse {
  quotas: TokenUsage[];
  envelope: QuotaEnvelope;
}

export interface ListQuotasParams {
  scope?: 'org' | 'team' | 'user';
  scope_id?: string;
  page?: number;
  per_page?: number;
}

export interface ListQuotasResponse {
  data: TokenUsage[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface CreateQuotaInput {
  scope: 'org' | 'team' | 'user';
  scope_id: string;
  model_id?: string | null;
  period_type: 'daily' | 'weekly' | 'monthly_calendar' | 'rolling_30' | 'custom';
  limit_input_tokens: number;
  limit_output_tokens: number;
  reset_strategy?: ResetStrategy;
}

export interface UpdateQuotaInput {
  limit_input_tokens?: number;
  limit_output_tokens?: number;
  period_type?: string;
  reset_strategy?: ResetStrategy;
}

export interface GrantOverrideInput {
  additional_input_tokens?: number;
  additional_output_tokens?: number;
  reason: string;
  expires_at?: string | null;
}

export interface GrantOverrideResponse {
  override_id: string;
  granted_by: string;
  reason: string;
  expires_at: string | null;
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export const quotasApi = {
  /**
   * GET /api/v1/me/quotas
   * Returns the current user's token usage and quota limits.
   */
  getMyQuotas(): Promise<GetMyQuotasResponse> {
    return apiClient.get<GetMyQuotasResponse>('/v1/me/quotas');
  },

  /**
   * GET /api/v1/quotas
   * List quotas (scoped by role). Admin sees all, maintainer sees team+own.
   */
  listQuotas(params?: ListQuotasParams): Promise<ListQuotasResponse> {
    const qs = new URLSearchParams();
    if (params?.scope) qs.set('scope', params.scope);
    if (params?.scope_id) qs.set('scope_id', params.scope_id);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.per_page) qs.set('per_page', String(params.per_page));
    const query = qs.toString();
    return apiClient.get<ListQuotasResponse>(`/v1/quotas${query ? `?${query}` : ''}`);
  },

  /**
   * POST /api/v1/quotas
   * Create a new quota.
   */
  createQuota(input: CreateQuotaInput): Promise<TokenUsage> {
    return apiClient.post<TokenUsage>('/v1/quotas', input);
  },

  /**
   * PATCH /api/v1/quotas/:id
   * Update quota limits.
   */
  updateQuota(id: string, input: UpdateQuotaInput): Promise<TokenUsage> {
    return apiClient.patch<TokenUsage>(`/v1/quotas/${id}`, input);
  },

  /**
   * DELETE /api/v1/quotas/:id
   * Delete a quota.
   */
  deleteQuota(id: string): Promise<void> {
    return apiClient.delete<void>(`/v1/quotas/${id}`);
  },

  /**
   * POST /api/v1/quotas/:id/override
   * Grant additional tokens (temporary or permanent).
   */
  grantOverride(id: string, input: GrantOverrideInput): Promise<GrantOverrideResponse> {
    return apiClient.post<GrantOverrideResponse>(`/v1/quotas/${id}/override`, input);
  },

  /**
   * DELETE /api/v1/quotas/:id/overrides/:overrideId
   * Revoke an active override.
   */
  revokeOverride(quotaId: string, overrideId: string): Promise<void> {
    return apiClient.delete<void>(`/v1/quotas/${quotaId}/overrides/${overrideId}`);
  },

  /**
   * POST /api/v1/quotas/:id/reset
   * Manually reset quota consumption to zero.
   */
  resetQuota(id: string): Promise<TokenUsage> {
    return apiClient.post<TokenUsage>(`/v1/quotas/${id}/reset`);
  },
};
